import React, { useState } from 'react';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Button } from 'primereact/button';
import { PermissionGroupUserDetails } from '../../../../../../../../api/types/settings/permissions/permission-group-details.type';
import { UserAvatarSmall } from '../../../../../../../../component/user-avatar/user-avatar-small';
import { WarningDialog } from '../../../../../../../../component/ui/warning-dialog/warning-dialog';
import { AddPermissionGroupUsersDialog } from './dialogs/add-permission-group-users-dialog';
import { removeUserFromPermissionGroupOperation } from '../../operations/remove-user-from-permission-group-operation';

interface PermissionGroupUsersTabProps {
  groupId: number;
  users: PermissionGroupUserDetails[];
  onUsersChanged: () => void;
}

export const PermissionGroupUsersTab: React.FC<
  PermissionGroupUsersTabProps
> = ({ groupId, users, onUsersChanged }) => {
  const [showAddUsersDialog, setShowAddUsersDialog] = useState(false);
  const [userToRemove, setUserToRemove] =
    useState<PermissionGroupUserDetails | null>(null);
  const [removingUser, setRemovingUser] = useState(false);

  const nameTemplate = (user: PermissionGroupUserDetails) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <UserAvatarSmall
        imageUrl={user.fileUrl ?? ''}
        firstName={user.firstName}
        lastName={user.lastName}
      />
      <span>
        {user.firstName} {user.lastName}
      </span>
    </div>
  );

  const actionsTemplate = (user: PermissionGroupUserDetails) => (
    <Button
      icon="pi pi-trash"
      className="p-button-rounded p-button-text p-button-danger"
      tooltip="הסרה מהקבוצה"
      tooltipOptions={{ position: 'top' }}
      onClick={() => setUserToRemove(user)}
    />
  );

  const handleConfirmRemove = async () => {
    if (!userToRemove) {
      return;
    }

    await removeUserFromPermissionGroupOperation(
      groupId,
      userToRemove.userId,
      setRemovingUser,
      () => {
        setUserToRemove(null);
        onUsersChanged();
      },
    );
  };

  return (
    <div style={{ direction: 'rtl', paddingTop: '12px' }}>
      <AddPermissionGroupUsersDialog
        visible={showAddUsersDialog}
        groupId={groupId}
        existingUsers={users}
        onHide={() => setShowAddUsersDialog(false)}
        onUsersAdded={onUsersChanged}
      />

      <WarningDialog
        isVisible={userToRemove !== null}
        header="הסרת משתמש מהקבוצה"
        addIcon
        content="האם אתה בטוח שברצונך להסיר את המשתמש מהקבוצה?"
        quoteName={
          userToRemove
            ? `${userToRemove.firstName} ${userToRemove.lastName}`
            : undefined
        }
        confirmLabel="הסרה"
        confirmLoading={removingUser}
        onHide={() => setUserToRemove(null)}
        onConfirm={handleConfirmRemove}
      />

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: '12px',
        }}
      >
        <Button
          label="הוספת משתמשים"
          icon="pi pi-plus"
          onClick={() => setShowAddUsersDialog(true)}
          style={{ height: '36px' }}
        />
      </div>

      <DataTable
        value={users}
        dataKey="userId"
        tableStyle={{ direction: 'rtl' }}
        emptyMessage={
          <div style={{ textAlign: 'right' }}>לא נמצאו משתמשים בקבוצה</div>
        }
      >
        <Column header="שם" body={nameTemplate} style={{ minWidth: '220px' }} />
        <Column field="nationalId" header="ת.ז" style={{ width: '140px' }} />
        <Column
          field="phoneNumber"
          header="טלפון"
          body={(user: PermissionGroupUserDetails) =>
            user.phoneNumber?.trim() || '—'
          }
          style={{ width: '140px' }}
        />
        <Column
          header=""
          body={actionsTemplate}
          style={{ width: '80px', textAlign: 'center' }}
        />
      </DataTable>
    </div>
  );
};
